import {observable} from 'mobx';
import {GUID} from '@mintdata/mint-kit/api/models/GUID';
import {IdUtil} from '@mintdata/mint-kit/impl/util/IDUtil';

export type ImportId = GUID;

export enum ImportStatus
{
    Uploading = 'Uploading',
    UploadFailed = 'UploadFailed',
    UploadSuccess = 'UploadSuccess',
    UploadCancelled = 'UploadCancelled',
    Importing = 'Importing',
    ImportFailed = 'ImportFailed',
    ImportSuccess = 'ImportSuccess',
    ImportCancelled = 'ImportCancelled',
}

export enum ImportFailedReasonEnum
{
    MultipleIsNotAllowed = 'MultipleIsNotAllowed',
    FileTypeIsNotSupported = 'FileTypeIsNotSupported',
    ImportIsInProgress = 'ImportIsInProgress'
}

export type ImportFailedReason = ImportFailedReasonEnum | string;

export class ImportTab
{
    @observable
    public name:string;

    @observable
    public shouldImport:boolean;

    @observable
    public shouldOverwrite:boolean;

    @observable
    public csvDelimiter:string;

    public constructor(name:string,
                       shouldImport:boolean = true,
                       shouldOverwrite:boolean = false,
                       csvDelimiter:string = ',')
    {
        this.name = name;
        this.shouldImport = shouldImport;
        this.shouldOverwrite = shouldOverwrite;
        this.csvDelimiter = csvDelimiter;
    }
}

export class ImportInstance
{
    public readonly id:ImportId;

    public readonly file:File;

    public readonly isCloud:boolean;

    @observable
    public status:ImportStatus;

    @observable
    public reason:ImportFailedReason;

    @observable
    public uploadProgress:number = 0; // [0...100]

    /**
     * ID which is returned in a successful file upload response.
     * It will be used in a request for parsing a file.
     */
    public requestId:GUID = null;

    @observable
    public tabs:ImportTab[];

    public constructor(file:File,
                       status:ImportStatus,
                       reason:ImportFailedReason)
    {
        this.id = IdUtil.newGUID();

        this.file = file;
        this.status = status;
        this.reason = reason;
    }

    public get isUploading():boolean
    {
        return this.status === ImportStatus.Uploading;
    }

    public get isUploadFailed():boolean
    {
        return this.status === ImportStatus.UploadFailed;
    }

    public get isUploadSuccess():boolean
    {
        return this.status === ImportStatus.UploadSuccess;
    }

    public get isUploadCancelled():boolean
    {
        return this.status === ImportStatus.UploadCancelled;
    }

    public get isImporting():boolean
    {
        return this.status === ImportStatus.Importing;
    }

    public get isImportFailed():boolean
    {
        return this.status === ImportStatus.ImportFailed;
    }

    public get isImportSuccess():boolean
    {
        return this.status === ImportStatus.ImportSuccess;
    }

    public get isImportCancelled():boolean
    {
        return this.status === ImportStatus.ImportCancelled;
    }

    public get isInProgress():boolean
    {
        return this.isUploading || this.isUploadSuccess || this.isImporting;
    }

    public get isCsvFile():boolean
    {
        return ['text/csv', 'text/tab-separated-values'].includes(this.file.type);
    }

    public get csvDelimiterByFileType():string
    {
        if (this.file.type === 'text/tab-separated-values')
        {
            return "\t";
        }

        return ',';
    }
}

export class ImportState
{
    @observable
    public instances:Map<ImportId, ImportInstance> = new Map();
}
